'use client';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Eye, EyeOff, ChevronRight, UserPlus } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useGlobalStore } from '@/store/useGlobalStore';
import { motion } from 'framer-motion';
import I18nProvider from '@/components/I18nProvider';
import ToastManager from '@/components/ToastManager';
import api, { fetchDesignAndWS, applyDesignFavicon } from '@/utils/api';
import PasswordStrengthIndicator from '@/components/ui/PasswordStrengthIndicator';
import { useTranslation } from 'react-i18next';

interface RegisterFormData {
  fullName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
}

interface DesignConfig {
  logo?: string;
  title?: string;
  favicon?: string;
}

export default function RegisterScreen() {
  const router = useRouter();
  const { addToast } = useGlobalStore();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [designConfig, setDesignConfig] = useState<DesignConfig>(() => {
    try {
      const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('design') : null;
      if (raw) {
        const d = JSON.parse(raw);
        const logo = (d?.logo_dark as string) || (d?.logo as string) || '';
        applyDesignFavicon(d?.favicon as string);
        return { logo, title: d?.title || d?.company_name || '', favicon: d?.favicon || '' };
      }
    } catch { /* ignore */ }
    return {};
  });

  useEffect(() => {
    fetchDesignAndWS()
      .then(() => {
        try {
          const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('design') : null;
          if (raw) {
            const design = JSON.parse(raw);
            applyDesignFavicon(design?.favicon as string);
            setDesignConfig({
              logo: (design?.logo_dark as string) || (design?.logo as string) || '',
              title: design?.title || design?.company_name || '',
              favicon: design?.favicon || '',
            });
          }
        } catch {
          // ignore
        }
      })
      .catch(() => {});
  }, []);

  const { register, handleSubmit, formState: { errors }, watch } = useForm<RegisterFormData>();

  const passwordValue = watch('password', '');

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    try {
      const res = await api.post('auth/register', {
        name: data.fullName,
        email: data.email,
        password: data.password,
        password_confirmed: data.confirmPassword,
        phone: data.phone,
      });
      const resData = res.data;
      const code = resData?.code ?? resData?.status;

      if (code === 200 || resData?.success) {
        addToast({ type: 'success', message: 'Account created! Please sign in.' });
        setTimeout(() => router.push('/login'), 1500);
      } else {
        const errors = resData?.result || resData?.errors || {};
        if (typeof errors === 'object') {
          Object.values(errors).forEach((msg) => {
            if (typeof msg === 'string') addToast({ type: 'error', message: msg });
          });
        } else {
          addToast({ type: 'error', message: resData?.message || 'Registration failed.' });
        }
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string; result?: Record<string, string> } } };
      const errData = axiosErr?.response?.data;
      if (errData?.result && typeof errData.result === 'object') {
        Object.values(errData.result).forEach((msg) => {
          if (typeof msg === 'string') addToast({ type: 'error', message: msg });
        });
      } else {
        addToast({ type: 'error', message: errData?.message || 'Registration failed. Please try again.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <I18nProvider>
      <div className="min-h-screen flex flex-col relative overflow-hidden" style={{ background: 'var(--background)' }}>
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 50% -10%, rgba(245,158,11,0.10) 0%, transparent 60%)' }}
        />

        {/* Top bar */}
        <div className="flex items-center px-5 pt-12 pb-4 relative" style={{ zIndex: 300 }}>
          <button
            onClick={() => router.back()}
            className="flex items-center gap-1.5 text-sm text-muted-foreground transition-all active:scale-95"
          >
            <ArrowLeft size={18} />
            {t('back')}
          </button>
        </div>

        <div className="flex-1 px-5 pb-10 relative z-10 overflow-y-auto scrollbar-hide">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Company Logo */}
            <div className="flex flex-col items-center mb-6">
              {designConfig.logo ? (
                <div className="w-16 h-16 rounded-3xl overflow-hidden mb-3 flex items-center justify-center" style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}>
                  <img src={designConfig.logo} alt={`${designConfig.title || ''} logo`} className="w-full h-full object-contain" />
                </div>
              ) : (
                <div
                  className="w-16 h-16 rounded-3xl flex items-center justify-center mb-3"
                  style={{ background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.25)' }}
                >
                  <UserPlus size={28} style={{ color: 'var(--primary)' }} />
                </div>
              )}
              {designConfig.title && <h2 className="text-lg font-bold text-foreground">{designConfig.title}</h2>}
            </div>

            <h1 className="text-2xl font-bold text-foreground mb-1">{t('createAccount')}</h1>
            <p className="text-sm text-muted-foreground mb-8">{t('fillDetails')}</p>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {/* Full Name */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">{t('name')}</label>
                <input
                  type="text"
                  autoComplete="name"
                  placeholder={t('fullNamePlaceholder')}
                  className="w-full px-4 py-3.5 rounded-2xl text-sm text-foreground placeholder-muted-foreground outline-none transition-all"
                  style={{
                    background: 'var(--input)',
                    border: `1px solid ${errors.fullName ? 'var(--negative)' : 'var(--border)'}`,
                  }}
                  onFocus={(e) => (e.target.style.borderColor = 'var(--primary)')}
                  onBlur={(e) => (e.target.style.borderColor = errors.fullName ? 'var(--negative)' : 'var(--border)')}
                  {...register('fullName', { required: t('name') + ' is required' })}
                />
                {errors.fullName && <p className="text-negative text-xs mt-1.5">{errors.fullName.message}</p>}
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">{t('email')}</label>
                <input
                  type="email"
                  autoComplete="email"
                  placeholder={t('emailPlaceholder')}
                  className="w-full px-4 py-3.5 rounded-2xl text-sm text-foreground placeholder-muted-foreground outline-none transition-all"
                  style={{
                    background: 'var(--input)',
                    border: `1px solid ${errors.email ? 'var(--negative)' : 'var(--border)'}`,
                  }}
                  onFocus={(e) => (e.target.style.borderColor = 'var(--primary)')}
                  onBlur={(e) => (e.target.style.borderColor = errors.email ? 'var(--negative)' : 'var(--border)')}
                  {...register('email', {
                    required: t('email') + ' is required',
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email address' },
                  })}
                />
                {errors.email && <p className="text-negative text-xs mt-1.5">{errors.email.message}</p>}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">{t('phone')}</label>
                <input
                  type="tel"
                  autoComplete="tel"
                  placeholder={t('phonePlaceholder')}
                  className="w-full px-4 py-3.5 rounded-2xl text-sm text-foreground placeholder-muted-foreground outline-none transition-all"
                  style={{
                    background: 'var(--input)',
                    border: `1px solid ${errors.phone ? 'var(--negative)' : 'var(--border)'}`,
                  }}
                  onFocus={(e) => (e.target.style.borderColor = 'var(--primary)')}
                  onBlur={(e) => (e.target.style.borderColor = errors.phone ? 'var(--negative)' : 'var(--border)')}
                  {...register('phone', { required: t('phone') + ' is required' })}
                />
                {errors.phone && <p className="text-negative text-xs mt-1.5">{errors.phone.message}</p>}
              </div>

              {/* Password */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">{t('password')}</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder={t('minCharsPlaceholder')}
                    className="w-full px-4 py-3.5 pr-12 rounded-2xl text-sm text-foreground placeholder-muted-foreground outline-none transition-all"
                    style={{
                      background: 'var(--input)',
                      border: `1px solid ${errors.password ? 'var(--negative)' : 'var(--border)'}`,
                    }}
                    onFocus={(e) => (e.target.style.borderColor = 'var(--primary)')}
                    onBlur={(e) => (e.target.style.borderColor = errors.password ? 'var(--negative)' : 'var(--border)')}
                    {...register('password', {
                      required: t('password') + ' is required',
                      minLength: { value: 8, message: 'At least 8 characters required' },
                    })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.password && <p className="text-negative text-xs mt-1.5">{errors.password.message}</p>}
                <PasswordStrengthIndicator password={passwordValue} />
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">{t('confirmPassword')}</label>
                <div className="relative">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder={t('repeatPasswordPlaceholder')}
                    className="w-full px-4 py-3.5 pr-12 rounded-2xl text-sm text-foreground placeholder-muted-foreground outline-none transition-all"
                    style={{
                      background: 'var(--input)',
                      border: `1px solid ${errors.confirmPassword ? 'var(--negative)' : 'var(--border)'}`,
                    }}
                    onFocus={(e) => (e.target.style.borderColor = 'var(--primary)')}
                    onBlur={(e) => (e.target.style.borderColor = errors.confirmPassword ? 'var(--negative)' : 'var(--border)')}
                    {...register('confirmPassword', {
                      required: 'Please confirm your password',
                      validate: (val) => val === watch('password') || 'Passwords do not match',
                    })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground"
                  >
                    {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.confirmPassword && <p className="text-negative text-xs mt-1.5">{errors.confirmPassword.message}</p>}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-4 rounded-2xl text-sm font-bold text-primary-foreground transition-all active:scale-95 flex items-center justify-center gap-2 mt-2"
                style={{
                  background: isLoading ? 'rgba(245,158,11,0.6)' : 'var(--primary)',
                  boxShadow: isLoading ? 'none' : '0 4px 20px rgba(245,158,11,0.3)',
                }}
              >
                {isLoading ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full"
                    />
                    {t('creatingAccount')}
                  </>
                ) : (
                  <>{t('createAccount')} <ChevronRight size={16} /></>
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <span className="text-sm text-muted-foreground">{t('alreadyHaveAccount')} </span>
              <button
                type="button"
                onClick={() => router.push('/login')}
                className="text-sm font-semibold"
                style={{ color: 'var(--primary)' }}
              >
                {t('signIn')}
              </button>
            </div>
          </motion.div>
        </div>
        <ToastManager />
      </div>
    </I18nProvider>
  );
}
